import { useState, type ReactNode } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PowerIcon from "@mui/icons-material/Power";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import BoltIcon from "@mui/icons-material/Bolt";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import BusinessIcon from "@mui/icons-material/Business";
import EvStationIcon from "@mui/icons-material/EvStation";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PlaceIcon from "@mui/icons-material/Place";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DescriptionIcon from "@mui/icons-material/Description";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useAuth0 } from "@auth0/auth0-react";
import type { PaletteMode } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { View } from "../types";

const SIDEBAR_WIDTH = 232;

const titles: Record<View, string> = {
  dashboard: "Dashboard",
  chargers: "Chargers",
  charger: "Charger Detail",
  maintenance: "Maintenance",
  onboarding: "Onboarding",
  transactions: "Transactions",
  drivers: "Drivers",
  driver: "Driver Detail",
  session: "Session Detail",
  "driver-groups": "Driver Groups",
  "charger-groups": "Charger Groups",
  vendors: "Vendors",
  "charger-models": "Charger Models",
  "config-templates": "Config Templates",
  locations: "Locations",
  location: "Location Detail",
  tariffs: "Tariffs",
  plans: "Plans",
  plan: "Plan Detail",
  contracts: "Contracts",
  roaming: "Roaming Connections",
  reports: "Financial Reports",
  "api-keys": "API Keys",
  team: "Organization",
};

interface NavItem {
  view: View;
  label: string;
  icon: ReactNode;
  group: "main" | "ocpp" | "biz" | "settings";
}

const items: NavItem[] = [
  { view: "dashboard", label: "Dashboard", icon: <DashboardIcon />, group: "main" },
  { view: "chargers", label: "Chargers", icon: <PowerIcon />, group: "ocpp" },
  { view: "maintenance", label: "Maintenance", icon: <BuildCircleIcon />, group: "ocpp" },
  { view: "onboarding", label: "Onboarding", icon: <HourglassEmptyIcon />, group: "ocpp" },
  { view: "transactions", label: "Transactions", icon: <BoltIcon />, group: "ocpp" },
  { view: "drivers", label: "Drivers", icon: <PeopleIcon />, group: "biz" },
  { view: "driver-groups", label: "Driver Groups", icon: <GroupIcon />, group: "biz" },
  { view: "charger-groups", label: "Charger Groups", icon: <PowerSettingsNewIcon />, group: "biz" },
  { view: "vendors", label: "Vendors", icon: <BusinessIcon />, group: "biz" },
  { view: "charger-models", label: "Charger Models", icon: <EvStationIcon />, group: "biz" },
  { view: "config-templates", label: "Config Templates", icon: <SettingsSuggestIcon />, group: "biz" },
  { view: "locations", label: "Locations", icon: <PlaceIcon />, group: "biz" },
  { view: "tariffs", label: "Tariffs", icon: <LocalOfferIcon />, group: "biz" },
  { view: "plans", label: "Plans", icon: <AssignmentIcon />, group: "biz" },
  { view: "contracts", label: "Contracts", icon: <HandshakeIcon />, group: "biz" },
  { view: "roaming", label: "Roaming", icon: <CompareArrowsIcon />, group: "biz" },
  { view: "reports", label: "Financial Reports", icon: <DescriptionIcon />, group: "biz" },
  { view: "api-keys", label: "API Keys", icon: <VpnKeyIcon />, group: "settings" },
  { view: "team", label: "Organization", icon: <PeopleAltIcon />, group: "settings" },
];

interface Props {
  view: View;
  onNavigate: (v: View, id?: number) => void;
  themeMode: PaletteMode;
  onToggleThemeMode: () => void;
  children: ReactNode;
}

export default function Layout({ view, onNavigate, themeMode, onToggleThemeMode, children }: Props) {
  const { user, logout } = useAuth0();
  const theme = useTheme();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const navItemSx = {
    color: "rgba(255,255,255,0.85)",
    borderRadius: 2,
    mx: 1.25,
    "&.Mui-selected": {
      bgcolor: alpha(theme.palette.primary.main, 0.24),
      color: "#fff",
    },
    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.15) },
    "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
  };

  const groupHeader = (label: string) => (
    <Typography
      variant="overline"
      sx={{ px: 3, pt: 2, display: "block", color: "rgba(255,255,255,0.5)" }}
    >
      {label}
    </Typography>
  );

  const renderGroup = (group: NavItem["group"]) =>
    items
      .filter((i) => i.group === group)
      .map((i) => (
        <ListItemButton
          key={i.view}
          selected={view === i.view}
          onClick={() => onNavigate(i.view)}
          sx={navItemSx}
        >
          <ListItemIcon>{i.icon}</ListItemIcon>
          <ListItemText primary={i.label} />
        </ListItemButton>
      ));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          bgcolor: theme.palette.primary.dark,
          backgroundImage:
            "linear-gradient(180deg, rgba(47,128,255,0.12) 0%, rgba(11,31,58,1) 35%)",
          color: "#fff",
          position: "fixed",
          top: 0,
          left: isSidebarVisible ? 0 : `-${SIDEBAR_WIDTH}px`,
          bottom: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          transition: theme.transitions.create("left", {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Toolbar sx={{ px: 3, alignItems: "baseline", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Zennet
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
            CPO
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <Box sx={{ flexGrow: 1 }}>
          <List dense>{renderGroup("main")}</List>
          {groupHeader("OCPP Service")}
          <List dense>{renderGroup("ocpp")}</List>
          {groupHeader("Business Service")}
          <List dense>{renderGroup("biz")}</List>
          {groupHeader("Settings")}
          <List dense>{renderGroup("settings")}</List>
        </Box>

        {/* User profile + logout */}
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={user?.picture}
            alt={user?.name}
            sx={{ width: 32, height: 32, fontSize: 14 }}
          >
            {user?.name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            <Typography variant="body2" noWrap sx={{ color: "#fff", fontWeight: 500, lineHeight: 1.2 }}>
              {user?.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {user?.email}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <LogoutIcon
              fontSize="small"
              sx={{ color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "#fff" } }}
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: isSidebarVisible ? `${SIDEBAR_WIDTH}px` : 0,
          p: 4,
          bgcolor: "background.default",
          transition: theme.transitions.create("margin-left", {
            duration: theme.transitions.duration.shortest,
          }),
        }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={isSidebarVisible ? "Hide side panel" : "Show side panel"}>
              <IconButton
                size="small"
                onClick={() => setIsSidebarVisible((visible) => !visible)}
                sx={{ color: "text.primary" }}
              >
                {isSidebarVisible ? <MenuOpenIcon /> : <MenuIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
              {titles[view]}
            </Typography>
          </Box>
          <Tooltip title={themeMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              size="small"
              onClick={onToggleThemeMode}
              sx={{ color: theme.palette.mode === "dark" ? "warning.main" : "text.primary" }}
            >
              {themeMode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        {children}
      </Box>
    </Box>
  );
}
